import { FC, useState } from 'react';
import {
  Datagrid,
  DateField,
  EmailField,
  ListContextProvider,
  Pagination,
  TextField,
  useList,
  Button,
  useRecordContext, RaRecord, WrapperField, ImageField,
} from 'react-admin';
import { MenuItem, Select, TextField as MuiTextField, Table, TableBody, TableCell, TableRow, FormControl, InputLabel } from '@mui/material';
import { useHttpClient } from 'shared/hooks/useHttpClient';
import { API_URL } from 'shared/constants/api-url';
import {
  AdminB2BPaginatedPatientsResponseDto,
  AdminB2BPatientResponseDto,
  AdminSessionBalanceResponseDto, AdminSessionBalanceUsageResponseDto
} from 'shared/api';
import { LABELS } from 'shared/constants/labels';
import { FunctionField } from 'react-admin';
import { Promocodes } from './promocodes';

interface Props {
  companyId: string;
}

enum PAGE {
  PATIENT_LIST = 1,
  PATIENT_DETAILS = 2,
  PATIENT_BALANCE_DETAIL_LIST = 3,
  PATIENT_BALANCE_COMMON_STATS = 4,
  PATIENT_PROMOCODES = 5,
}

interface PatientListViewProps {
  searchEmail: string;
  isAuthorized: string;
  handleUpdateSearchEmail: (value: string) => void;
  handleAuthorizeChange: (value: string) => void;
  patientsListContext: ReturnType<typeof useList>;
  patientsPage: number;
  patientsPerPage: number;
  isLoadingPatients: boolean;
  setPatientsPage: (page: number) => void;
  setPatientsPerPage: (perPage: number) => void;
  ChangePageButton: FC<{ page: PAGE, label: string }>;
}

const PatientListView: FC<PatientListViewProps> = ({
   searchEmail,
   isAuthorized,
   handleUpdateSearchEmail,
   handleAuthorizeChange,
   patientsListContext,
   patientsPage,
   patientsPerPage,
   isLoadingPatients,
   setPatientsPage,
   setPatientsPerPage,
   ChangePageButton
}) => {
  const record = useRecordContext();

  return (
    <>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <MuiTextField
          label="Email"
          placeholder="Email"
          value={searchEmail}
          onChange={(e) => handleUpdateSearchEmail(e.target.value)}
          variant="standard"
        />
        <FormControl variant="standard" sx={{ minWidth: 200 }}>
          <InputLabel id="auth-label">Вошел в ЛК</InputLabel>
          <Select
            labelId="auth-label"
            value={isAuthorized}
            onChange={(e) => handleAuthorizeChange(e.target.value)}
          >
            <MenuItem value="null">Не имеет значения</MenuItem>
            <MenuItem value="true">Да</MenuItem>
            <MenuItem value="false">Нет</MenuItem>
          </Select>
        </FormControl>
      </div>
      <ListContextProvider value={patientsListContext}>
        <Datagrid bulkActionButtons={false}>
          <TextField source="firstname" sortable={false} label={LABELS.firstname} />
          <EmailField source="email" label={LABELS.email} sortable={false} />
          <TextField source="companyGroup" label={LABELS.companyGroup} sortable={false} />
          <TextField source="companyRole" label={LABELS.companyRole} sortable={false} />
          <DateField source="dateOfBirth" label={LABELS.dateOfBirth} sortable={false} />
          <DateField source="createdAt" label="Зарегистрирован" sortable={false} showTime />
          <ChangePageButton page={PAGE.PATIENT_DETAILS} label="Подробнее" />
          {record.type === 'balance' && <ChangePageButton page={PAGE.PATIENT_BALANCE_DETAIL_LIST} label="Баланс" />}
          {record.type === 'promocode' && <ChangePageButton page={PAGE.PATIENT_PROMOCODES} label="Промокоды" />}
        </Datagrid>
        <Pagination
          isLoading={isLoadingPatients}
          page={patientsPage}
          perPage={patientsPerPage}
          total={patientsListContext.total}
          setPage={setPatientsPage}
          setPerPage={setPatientsPerPage}
        />
      </ListContextProvider>
    </>
  );
};

const CompanyPatients: FC<Props> = ({ companyId }) => {
  const [patientsPage, setPatientsPage] = useState(1);
  const [patientsPerPage, setPatientsPerPage] = useState(10);
  const [searchEmail, setSearchEmail] = useState('');
  const [isAuthorized, setIsAuthorized] = useState('null');
  
  const [balancePage, setBalancePage] = useState(1);
  const [balancePerPage, setBalancePerPage] = useState(10);

  const { data: patientsData, isLoading: isLoadingPatients } = useHttpClient<AdminB2BPaginatedPatientsResponseDto>(
    `${API_URL.companyPatients(companyId)}?limit=${patientsPerPage}&page=${patientsPage}${!!searchEmail ? `&email=${encodeURIComponent(searchEmail)}` : ''}${isAuthorized !== 'null' ? `&isAuthorized=${isAuthorized}` : ''}`,
  );

  const [currentPage, setCurrentPage] = useState(PAGE.PATIENT_LIST);
  const [patientId, setPatientId] = useState<string | null>(null);
  
  const { data: patientDetails, isLoading: isLoadingPatientDetails } = useHttpClient<AdminB2BPatientResponseDto>(
    patientId ? `${API_URL.companyPatientById(companyId, patientId)}` : '',
  );

  const { data: balanceListData, isLoading: isLoadingBalanceList } = useHttpClient<{ data: AdminSessionBalanceResponseDto[], total: number }>(
    patientId ? `${API_URL.companyPatientBalanceList(companyId, patientId)}?limit=${balancePerPage}&page=${balancePage}` : '',
  );

  const baseContext = useList<RaRecord>({
    data: patientsData?.data ?? [],
    isLoading: isLoadingPatients,
    page: patientsPage,
    perPage: patientsPerPage,
    sort: { field: 'lastname', order: 'DESC' },
  });

  const patientsListContext = {
    ...baseContext,
    total: patientsData?.total ?? 0,
  };

  const balanceListContext = useList({
    data: balanceListData?.data ?? [],
    isLoading: isLoadingBalanceList,
    page: balancePage,
    perPage: balancePerPage,
    sort: { field: 'startWith', order: 'DESC' },
  });

  const {data: balanceCommonStatsData, isLoading: isLoadingBalanceCommonStats } = useHttpClient<AdminSessionBalanceUsageResponseDto>(
    patientId ? `${API_URL.companyPatientBalanceStats(companyId, patientId)}` : '',
  );
  const handleUpdateSearchEmail = (value: string) => {
    setSearchEmail(value);
    setPatientsPage(1);
  };

  const handleAuthorizeChange = (value: string) => {
    setIsAuthorized(value);
    setPatientsPage(1);
  };

  const ChangePageButton = (props: { page: PAGE, label: string }) => {
    const record = useRecordContext() as AdminB2BPatientResponseDto;

    if (!record) {
      return null;
    }

    return (
      <Button 
        label={props.label} 
        onClick={() => handleShowButtonClick(props.page, record.id)}
      />
    );
  };

  const handleShowButtonClick = (newPage: PAGE, id?: string) => {
    if (id) {
      setPatientId(id);
    }

    setCurrentPage(newPage);
  };

  const isActive = (page: PAGE) => currentPage === page;

  const getStyle = (page: PAGE) =>
    isActive(page)
      ? { backgroundColor: '#1976d2', color: 'white', border: '1px solid #1976d2' }
      : {};



const ButtonsView = () => {
    const record = useRecordContext();

    return (
      <div style={{ marginBottom: '1rem' }}>
        <Button
          label="Назад к списку сотрудников"
          onClick={() => {
            setCurrentPage(PAGE.PATIENT_LIST);
            setPatientId(null);
          }}
          style={getStyle(PAGE.PATIENT_LIST)}
        />
        <Button
          label="Просмотр пациента"
          onClick={() => setCurrentPage(PAGE.PATIENT_DETAILS)}
          style={getStyle(PAGE.PATIENT_DETAILS)}
        />
        {record.type === 'balance' && (
          <>
            <Button
              label="Общая статистика по балансам"
              onClick={() => setCurrentPage(PAGE.PATIENT_BALANCE_COMMON_STATS)}
              style={getStyle(PAGE.PATIENT_BALANCE_COMMON_STATS)}
            />
            <Button
              label="Детальный список балансов"
              onClick={() => setCurrentPage(PAGE.PATIENT_BALANCE_DETAIL_LIST)}
              style={getStyle(PAGE.PATIENT_BALANCE_DETAIL_LIST)}
            />
          </>
        )}
        {record.type === 'promocode' && (
          <Button
            label="Промокоды"
            onClick={() => setCurrentPage(PAGE.PATIENT_PROMOCODES)}
            style={getStyle(PAGE.PATIENT_PROMOCODES)}
          />
        )}
      </div>
    );
  };

  const PatientDetailsView = () => {
    if (isLoadingPatientDetails) {
      return <div>Загрузка...</div>;
    }

    if (!patientDetails) {
      return (<div>
        {<ButtonsView />}
        <div>Данные не найдены</div>
      </div>);
    }

    return (
      <div>
        {<ButtonsView />}
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{LABELS.firstname}</TableCell>
              <TableCell>{patientDetails.firstname}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{LABELS.email}</TableCell>
              <TableCell>
                <a href={`mailto:${patientDetails.email}`} style={{ color: '#1976d2' }}>{patientDetails.email}</a>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{LABELS.dateOfBirth}</TableCell>
              <TableCell>
                <DateField record={patientDetails} source="dateOfBirth" />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{LABELS.companyGroup}</TableCell>
              <TableCell>{patientDetails.companyGroup}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{LABELS.companyRole}</TableCell>
              <TableCell>{patientDetails.companyRole}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Дата регистрации</TableCell>
              <TableCell>
                <DateField record={patientDetails} source="createdAt" showTime />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <WrapperField label={LABELS.avatarKey}>
          <a rel="noreferrer" href={patientDetails.avatarKey} target="_blank">
            <ImageField
              record={patientDetails}
              source="avatarKey"
              sx={{
                '& .RaImageField-image': {
                  width: 300,
                  height: 300,
                  objectFit: 'cover',
                },
              }}
            />
          </a>
        </WrapperField>
      </div>
    );
  };

  const PatientBalanceDetailListView = () => {
    if (isLoadingBalanceList) {
      return <div>Загрузка...</div>;
    }

    if (!balanceListData?.data || balanceListData.data.length === 0) {
      return (
        <div>
          {<ButtonsView />}
          <div>Данные не найдены</div>
        </div>
      );
    }

    return (
      <div>
        {<ButtonsView />}

        <ListContextProvider value={balanceListContext}>
          <Datagrid bulkActionButtons={false}>
            <FunctionField
              label="ID баланса"
              render={(record: AdminSessionBalanceResponseDto) => record.id ?? '—'}
              sortable={false}
            />
            <FunctionField
              label="Email пользователя"
              render={(record: AdminSessionBalanceResponseDto) => record.user?.email ?? '—'}
              sortable={false}
            />
            <FunctionField
              label="Статус баланса"
              render={(record: AdminSessionBalanceResponseDto) => record.status ?? '—'}
              sortable={false}
            />
            <DateField
              source="startWith"
              label="Дата начала действия"
              sortable={false}
            />
            <DateField
              source="endWith"
              label="Дата окончания действия"
              sortable={false}
            />
            <DateField
              source="createdAt"
              label="Дата начисления"
              sortable={false}
            />
          </Datagrid>
          <Pagination
            isLoading={isLoadingBalanceList}
            page={balancePage}
            perPage={balancePerPage}
            total={balanceListData.total}
            setPage={setBalancePage}
            setPerPage={setBalancePerPage}
          />
        </ListContextProvider>
      </div>
    );
  };

  const PatientBalanceCommonStatsView = () => {
    if (isLoadingBalanceCommonStats) {
      return <div>Загрузка...</div>;
    }

    if (!balanceCommonStatsData) {
      return (
        <div>
          {<ButtonsView />}
          <div>Данные не найдены</div>
        </div>
      );
    }

    return (
      <div>
        {<ButtonsView />}

        <Table>
          <TableBody>
            <TableRow>
              <TableCell>Доступно</TableCell>
              <TableCell>{balanceCommonStatsData.available}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Использованно</TableCell>
              <TableCell>{balanceCommonStatsData.used}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Сгорело</TableCell>
              <TableCell>{balanceCommonStatsData.expired}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderPage = () => {
    switch (currentPage) {
      case PAGE.PATIENT_LIST:
        return (
          <PatientListView
            searchEmail={searchEmail}
            isAuthorized={isAuthorized}
            handleUpdateSearchEmail={handleUpdateSearchEmail}
            handleAuthorizeChange={handleAuthorizeChange}
            patientsListContext={patientsListContext}
            patientsPage={patientsPage}
            patientsPerPage={patientsPerPage}
            isLoadingPatients={!!isLoadingPatients}
            setPatientsPage={setPatientsPage}
            setPatientsPerPage={setPatientsPerPage}
            ChangePageButton={ChangePageButton}
          />
        );
      case PAGE.PATIENT_DETAILS:
        return <PatientDetailsView />;
      case PAGE.PATIENT_BALANCE_DETAIL_LIST:
        return <PatientBalanceDetailListView />;
      case PAGE.PATIENT_BALANCE_COMMON_STATS:
        return <PatientBalanceCommonStatsView />;
      case PAGE.PATIENT_PROMOCODES:
          return <Promocodes userId={patientDetails?.userId ?? ''} buttonsView={<ButtonsView />} />;
      default:
        return null;
    }
  };

  return renderPage();
}

export default CompanyPatients;
